<script setup lang="ts">
import CardContainer from '@/components/Card/card-container.vue'
import CardHeader from '@/components/Card/card-header.vue'
import InputToggleGroup from '@/components/input-toggle-group.vue'
import IconHelp from '@/shared/components/IconHelp.vue'
import CardContent from '@/components/Card/card-content.vue'
import { z } from 'zod'
import type { invoiceSchema } from '@/features/invoices/schemas'
import { formatNumber } from '@/helpers/formatting'
import IconChanged from '@/shared/components/IconChanged.vue'
import InputCheckbox from '@/components/input-checkbox.vue'
import { computed, reactive } from 'vue'
import InvoiceSkeletonPlaceholderText from '@/features/invoices/components/partials/InvoiceSkeletonPlaceholderText.vue'
import { MeterTypeNumeric } from '@/shared/constants'
import { createReusableTemplate } from '@vueuse/core'
import ButtonSecondary from '@/components/button-secondary.vue'
import { faLink } from '@fortawesome/pro-regular-svg-icons'
import { useModalState } from '@/shared/composables/useModalState'
import ModalLinkedMeters from '@/views/billing/invoicing/modal-linked-meters.vue'
import { InvoiceStatusNumeric } from '@/features/invoices/constants'

type TrackedChange<TItem> = { id: number, values: Partial<TItem> }
type MeterReading = z.infer<typeof invoiceSchema>['meterReadings'][number]
type MeterReadingWithNullableValues = Omit<
  MeterReading,
  'forwardActiveStartReading'|'forwardActiveEndReading'|'reverseActiveStartReading'|'reverseActiveEndReading'
> & {
  forwardActiveStartReading: number|null
  forwardActiveEndReading: number|null
  reverseActiveStartReading: number|null
  reverseActiveEndReading: number|null
}

const show = defineModel<boolean>('show')
const props = withDefaults(
  defineProps<{
    exported?: boolean
    meters: MeterReadingWithNullableValues[]
    invoiceId?: number
    invoiceStatus?: InvoiceStatusNumeric
    gridConsumption: number
    solarConsumption: number
    exportedEnergy?: number|null
  }>(),
  { exported: false }
)

const valuesModifiedTooltipText = 'This value was edited and will not align with the monthly solar report totals.'
const tracked = reactive<{ meters: TrackedChange<Pick<MeterReading, 'hide'>>[] }>({ meters: [] })
const [DefineReadingWithPlaceholder, ReadingWithPlaceholder] = createReusableTemplate<{ reading: number|null, modified: boolean }>()
const { open: showLinkedMeters, close: hideLinkedMeters, isOpen: isShowingLinkedMeters } = useModalState()

function trackChange<TItem>(collection: keyof typeof tracked, id: number, newValues: Partial<TItem>) {
  const copied = [...tracked[collection]]
  const modifiedIndex = copied.findIndex(x => x.id === id)

  if (modifiedIndex > -1) {
    copied[modifiedIndex].values = { ...copied[modifiedIndex].values, ...newValues }
  } else {
    copied.push({ id, values: newValues })
  }

  tracked[collection] = copied
}

defineExpose({
  changes() {
    return [...tracked.meters]
  },
  clearChanges() {
    tracked.meters = []
  }
})
</script>

<template>
  <ModalLinkedMeters v-if="invoiceId && isShowingLinkedMeters" :invoice-id="invoiceId" @close="hideLinkedMeters" />

  <DefineReadingWithPlaceholder v-slot="{ reading, modified }">
    <InvoiceSkeletonPlaceholderText v-if="reading === null" />
    <template v-else>
      <span class="invoice-details">{{ formatNumber(reading, '-') }}</span>
      <IconChanged class="ms-1" v-if="modified" :tooltip="valuesModifiedTooltipText" />
    </template>
  </DefineReadingWithPlaceholder>

  <CardContainer>
    <CardHeader uppercase>
      Meter Readings
      <span>
        <ButtonSecondary v-if="invoiceId"
                         :outline="invoiceStatus === InvoiceStatusNumeric.ActionRequired ? 'red' : 'cyan'"
                         :icon="faLink"
                         :class="$style['linked-meters']"
                         @click="showLinkedMeters"
                         size="small">Linked Meters</ButtonSecondary>
        <InputToggleGroup :options="[{ value: true, text: 'Show' }, { value: false, text: 'Hide' }]"
                          :selected="show"
                          @update:selected="show = $event" />
        <IconHelp class="ms-2" :tooltip="`Show or hide this\nsection on the invoice.`" />
      </span>
    </CardHeader>
    <CardContent>
      <table class="table">
        <thead>
          <tr>
            <th>Device ID</th>
            <th>Type</th>
            <th>Serial No.</th>
            <th>Start</th>
            <th>End</th>
            <th>Difference</th>
            <th>Hide</th>
          </tr>
        </thead>
        <tbody>
          <!-- Export values first. -->
          <template v-if="exported">
            <tr v-for="meter in meters.filter(x => x.meterTypeId === MeterTypeNumeric.Grid)" :key="`export|${meter.id}`">
              <td>{{ meter.deviceId }} - Export</td>
              <td>{{ meter.meterType.name }}</td>
              <td>{{ meter.serialNumber }}</td>
              <td>
                <ReadingWithPlaceholder :modified="meter.isReverseActiveStartReadingModified" :reading="meter.reverseActiveStartReading" />
              </td>
              <td>
                <ReadingWithPlaceholder :modified="meter.isReverseActiveEndReadingModified" :reading="meter.reverseActiveEndReading" />
              </td>
              <td>
                <InvoiceSkeletonPlaceholderText v-if="meter.reverseActiveEndReading === null || meter.reverseActiveStartReading === null" />
                <template v-else>{{ formatNumber(meter.reverseActiveEndReading - meter.reverseActiveStartReading) }}</template>
              </td>
              <td>
                <InputCheckbox @update:checked="trackChange('meters', meter.id, { hide: $event })" :checked="meter.hide" v-if="meter.meterTypeId === MeterTypeNumeric.Grid" />
              </td>
            </tr>
          </template>

          <!-- Normal meter readings -->
          <tr v-for="meter in [...meters].sort(x => +x.deviceId)" :key="meter.id">
            <td>{{ meter.deviceId }}</td>
            <td>{{ meter.meterType.name }}</td>
            <td>{{ meter.serialNumber }}</td>
            <td>
              <ReadingWithPlaceholder :modified="meter.isForwardActiveStartReadingModified" :reading="meter.forwardActiveStartReading" />
            </td>
            <td>
              <ReadingWithPlaceholder :modified="meter.isForwardActiveEndReadingModified" :reading="meter.forwardActiveEndReading" />
            </td>
            <td>
              <InvoiceSkeletonPlaceholderText v-if="meter.forwardActiveStartReading === null || meter.forwardActiveEndReading === null" />
              <template v-else>{{ formatNumber(meter.forwardActiveEndReading - meter.forwardActiveStartReading) }}</template>
            </td>
            <td>
              <InputCheckbox @update:checked="trackChange('meters', meter.id, { hide: $event })" :checked="meter.hide" v-if="meter.meterTypeId === MeterTypeNumeric.Grid || meter.meterTypeId === MeterTypeNumeric.VirtualGrid" />
            </td>
          </tr>
        </tbody>
      </table>

      <div :class="$style.summary">
        <span class="text-end">Grid Consumption (kWh)</span>
        <span>{{ formatNumber(gridConsumption) }}</span>
        <span class="text-end">PV Consumption (kWh)</span>
        <span>{{ formatNumber(solarConsumption) }}</span>
        <template v-if="typeof exportedEnergy === 'number'">
          <span class="text-end">Export Power (kWh)</span>
          <span>{{ formatNumber(exportedEnergy) }}</span>
        </template>
      </div>
    </CardContent>
  </CardContainer>
</template>

<style module lang="scss">
@import "@/assets/variables";

.summary {
  background: #eee;
  font-weight: 700;
  padding: 20px 20px 20px 64.5%;
  border-bottom-left-radius: #{$card-border-radius};
  border-bottom-right-radius: #{$card-border-radius};
  display: grid;
  grid-template-columns: 167px 1fr;
  column-gap: 78px;
  margin: #{$card-spacer-y} #{-1 * $card-spacer-x} #{-1 * $card-spacer-y};
}

.linked-meters {
  margin-right: 20px;
}
</style>
